'use client';

import type { EnterAuthStage, EnterAuthStageEventName } from '@enter/auth';
import type { MixpanelContextValue } from '@/contexts/mixpanel';

export type EnterAuthEntry =
    | 'home_header_signin'
    | 'home_header_signup'
    | 'home_signin_popup'
    | 'product_auth_gate'
    | 'direct_link';
export type EnterLoginIntent = 'login' | 'signup';
export type EnterLoginType = 'standard' | 'google_one_tap' | 'silent';

export interface EnterHomeAuthAttempt {
    authTraceId: string;
    entry: EnterAuthEntry;
    loginIntent: EnterLoginIntent;
    loginType: EnterLoginType;
    mixpanelDistinctId: string | null;
    startedAt: number;
}

const PENDING_ATTEMPT_KEY = 'enter_auth_analytics_pending';
const PENDING_ATTEMPT_MAX_AGE_MS = 10_000;

function isAttempt(value: unknown): value is EnterHomeAuthAttempt {
    if (!value || typeof value !== 'object') return false;
    const attempt = value as Partial<EnterHomeAuthAttempt>;
    return Boolean(
        attempt.authTraceId &&
        attempt.entry &&
        attempt.loginIntent &&
        attempt.loginType &&
        typeof attempt.startedAt === 'number'
    );
}

function persistPendingAttempt(attempt: EnterHomeAuthAttempt): void {
    try {
        window.sessionStorage.setItem(PENDING_ATTEMPT_KEY, JSON.stringify(attempt));
    } catch {
        // 存储不可用不能阻断登录。
    }
}

export function createEnterHomeAuthAttempt(input: {
    entry: EnterAuthEntry;
    loginIntent: EnterLoginIntent;
    loginType?: EnterLoginType;
    mixpanelDistinctId?: string | null;
    authTraceId?: string;
    persist?: boolean;
}): EnterHomeAuthAttempt {
    const attempt: EnterHomeAuthAttempt = {
        authTraceId: input.authTraceId?.trim() || crypto.randomUUID(),
        entry: input.entry,
        loginIntent: input.loginIntent,
        loginType: input.loginType ?? 'standard',
        mixpanelDistinctId: input.mixpanelDistinctId?.trim() || null,
        startedAt: Date.now(),
    };
    if (input.persist !== false) persistPendingAttempt(attempt);
    return attempt;
}

export function takePendingEnterHomeAuthAttempt(): EnterHomeAuthAttempt | null {
    try {
        const stored = window.sessionStorage.getItem(PENDING_ATTEMPT_KEY);
        window.sessionStorage.removeItem(PENDING_ATTEMPT_KEY);
        if (!stored) return null;
        const parsed: unknown = JSON.parse(stored);
        if (!isAttempt(parsed) || Date.now() - parsed.startedAt > PENDING_ATTEMPT_MAX_AGE_MS) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function encodeMixpanelDistinctId(value: string | null): string | undefined {
    if (!value || value.length > 191) return undefined;
    const bytes = new TextEncoder().encode(value);
    // Auth0 ext-* 值最多 255 字符；超长身份必须省略，不能截断成另一个身份。
    if (bytes.length > 191) return undefined;
    return btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export function createEnterHomeAuthAuthorizationParams(attempt: EnterHomeAuthAttempt) {
    return {
        'ext-source': 'codia-web',
        'ext-auth-trace-id': attempt.authTraceId,
        'ext-mixpanel-distinct-id': encodeMixpanelDistinctId(attempt.mixpanelDistinctId),
        'ext-entry': attempt.entry,
        'ext-login-intent': attempt.loginIntent,
        'ext-login-type': attempt.loginType,
        'ext-started-at': String(attempt.startedAt),
    };
}

export function appendEnterHomeAuthAnalytics(
    path: string,
    attempt: EnterHomeAuthAttempt,
): string {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('auth_entry', attempt.entry);
    url.searchParams.set('login_intent', attempt.loginIntent);
    url.searchParams.set('login_type', attempt.loginType);
    url.searchParams.set('auth_started_at', String(attempt.startedAt));
    return `${url.pathname}${url.search}${url.hash}`;
}

export function createEnterHomeAuthTelemetry(
    attempt: EnterHomeAuthAttempt,
    logger: MixpanelContextValue['logger'],
) {
    const stageStarts = new Map<EnterAuthStage, number>();
    const stageAttempts = new Map<EnterAuthStage, number>();
    let preparationState: 'prewarmed' | 'in_flight' | 'cold' | 'unavailable' = 'unavailable';

    return (eventName: EnterAuthStageEventName, stage: EnterAuthStage): void => {
        const now = Date.now();
        if (eventName === 'auth_stage_start') {
            stageStarts.set(stage, now);
            stageAttempts.set(stage, (stageAttempts.get(stage) ?? 0) + 1);
            if (stage === 'fingerprint_collect') preparationState = 'cold';
        }

        const common = {
            auth_trace_id: attempt.authTraceId,
            stage,
            entry: attempt.entry,
            login_intent: attempt.loginIntent,
            login_type: attempt.loginType,
            preparation_state: preparationState,
            ...(attempt.mixpanelDistinctId
                ? { mixpanel_distinct_id: attempt.mixpanelDistinctId }
                : {}),
        };
        logger(
            eventName,
            eventName === 'auth_stage_success'
                ? {
                      ...common,
                      duration_ms: Math.max(0, now - (stageStarts.get(stage) ?? now)),
                      attempt: stageAttempts.get(stage) ?? 1,
                  }
                : common,
            { sendImmediately: true },
        );
    };
}
